"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { UserPill } from "@privy-io/react-auth/ui";
import { useUserMe } from "@/contexts/UserMeContext";

/**
 * SafeUserPill — wraps Privy's UserPill with empty-render detection.
 * If UserPill renders without visible content (corrupted session), shows fallback.
 *
 * Also overrides Privy's default label (which surfaces the connected medium
 * — frenflowdev@gmail.com / 0x1234… — and reads like an account-management
 * widget) with the user's FrenFlow `@username`. Falls back to display name
 * → Privy default when neither is set yet (fresh signups before profile edit).
 */
export function SafeUserPill({ fallback }: { fallback: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);
  const { user } = useUserMe();

  useEffect(() => {
    if (!ref.current) return;
    // Check after a short delay to let UserPill finish internal rendering
    const timer = setTimeout(() => {
      if (ref.current) {
        const hasVisibleContent = ref.current.offsetWidth > 10 && ref.current.offsetHeight > 10;
        const hasButton = ref.current.querySelector('button, a, [role="button"]');
        if (!hasVisibleContent || !hasButton) {
          setIsEmpty(true);
        }
      }
    }, 2_000);
    return () => clearTimeout(timer);
  }, []);

  if (isEmpty) return <>{fallback}</>;

  const u = user as { username?: string | null; name?: string | null } | null;
  const frenflowLabel = u?.username
    ? `@${u.username}`
    : (u?.name?.trim() || undefined);

  return (
    <div ref={ref} data-privy-pill className="[&_button]:!rounded-[5px] [&_div]:!rounded-[5px]">
      <UserPill label={frenflowLabel} />
    </div>
  );
}
